import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../../services/api';
import { AI_ACTION_TYPE_VALUES, AI_ACTION_LABELS } from '@claimup/shared/constants/ai-agent';
import { expedientStatusLabel } from '@claimup/shared/constants/expedient';

/**
 * El «Historial» de la revisión de la IA del cliente (docs/ar-ia-historial-plan.md).
 *
 * Todo lo que ha decidido el agente en sus expedientes AR, una fila por acción,
 * salga solo o aprobado. Pedido por Edgar: al final del día, filtrar por
 * «Cambiar estado» y ver en qué expedientes y a qué estado.
 */

export const DECISIONES = {
  auto: 'Salió sola',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  manual: 'Lo hizo una persona',
  pending: 'Esperando revisión',
};

const FILTRO_DECISION = [
  { value: '', label: 'Todas' },
  { value: 'executed', label: 'Las que salieron (solas o aprobadas)' },
  ...Object.entries(DECISIONES).map(([value, label]) => ({ value, label })),
];

/** Lo que hizo la acción, en una línea. */
export function detalle(fila) {
  const p = fila.params || {};
  switch (fila.action_type) {
    case 'update_expedient_status':
      return `→ ${expedientStatusLabel(p.new_status) || '?'}${p.reason ? ` · ${p.reason}` : ''}`;
    case 'send_whatsapp':
    case 'send_sms':
      return p.message || '';
    case 'send_email':
      return p.subject || p.body || '';
    case 'create_annotation':
      return p.text || '';
    case 'create_follow_up_assignment':
      return [p.description, p.days_from_now != null ? `en ${p.days_from_now} días` : null].filter(Boolean).join(' · ');
    case 'cancel_assignment':
      return p.reason || '';
    // No lleva parámetros: la plantilla la elige el código según el peldaño.
    case 'send_whatsapp_template':
      return 'La plantilla que toca en la secuencia';
    default:
      return '';
  }
}

const hoy = () => moment().format('YYYY-MM-DD');

const ArHistory = () => {
  const [filtros, setFiltros] = useState({ from: hoy(), to: hoy(), action_type: '', decision: '' });
  const [pagina, setPagina] = useState(1);
  const [datos, setDatos] = useState({ items: [], total: 0, pageSize: 50 });

  const cargar = useCallback(async () => {
    const params = Object.fromEntries(Object.entries({ ...filtros, page: pagina, pageSize: 50 }).filter(([, v]) => v));
    try {
      const { data } = await api.get('/ar-review/history', { params });
      setDatos(data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }, [filtros, pagina]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiar = (campo, valor) => {
    setPagina(1);
    setFiltros(f => ({ ...f, [campo]: valor }));
  };

  const paginas = Math.max(1, Math.ceil((datos.total || 0) / (datos.pageSize || 50)));

  return (
    <div className='bg-white border border-slate-200 rounded-lg p-4 space-y-4'>
      <div className='flex flex-wrap items-end gap-3 text-sm'>
        <label className='flex flex-col text-slate-600'>
          Desde
          <input
            type='date'
            value={filtros.from}
            onChange={e => cambiar('from', e.target.value)}
            className='mt-1 rounded-lg border border-slate-300 px-2 py-1'
          />
        </label>
        <label className='flex flex-col text-slate-600'>
          Hasta
          <input
            type='date'
            value={filtros.to}
            onChange={e => cambiar('to', e.target.value)}
            className='mt-1 rounded-lg border border-slate-300 px-2 py-1'
          />
        </label>
        <label className='flex flex-col text-slate-600'>
          Acción
          <select
            value={filtros.action_type}
            onChange={e => cambiar('action_type', e.target.value)}
            className='mt-1 rounded-lg border border-slate-300 px-2 py-1'
          >
            <option value=''>Todas</option>
            {AI_ACTION_TYPE_VALUES.map(t => (
              <option key={t} value={t}>
                {AI_ACTION_LABELS[t] || t}
              </option>
            ))}
          </select>
        </label>
        <label className='flex flex-col text-slate-600'>
          Decisión
          <select
            value={filtros.decision}
            onChange={e => cambiar('decision', e.target.value)}
            className='mt-1 rounded-lg border border-slate-300 px-2 py-1'
          >
            {FILTRO_DECISION.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <span className='ml-auto text-slate-500'>{datos.total} decisiones</span>
      </div>

      {datos.items.length === 0 ? (
        <p className='text-sm text-slate-500'>No hay decisiones con esos filtros.</p>
      ) : (
        <div className='overflow-x-auto'>
          <table className='min-w-full text-sm'>
            <thead>
              <tr className='text-left text-xs uppercase text-slate-500'>
                <th className='py-2 pr-3'>Cuándo</th>
                <th className='py-2 pr-3'>Deudor</th>
                <th className='py-2 pr-3'>Acción</th>
                <th className='py-2 pr-3'>Qué hizo</th>
                <th className='py-2 pr-3'>Decisión</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {datos.items.map(f => (
                <tr key={f.key}>
                  <td className='py-2 pr-3 whitespace-nowrap text-slate-600'>
                    {moment(f.created_at).format('DD/MM HH:mm')}
                  </td>
                  <td className='py-2 pr-3'>
                    <a href={`/view/${f.expedient_id}`} target='_blank' rel='noreferrer' className='text-blue-600 hover:underline'>
                      {f.summary?.deudor || 'Expediente'}
                    </a>
                  </td>
                  <td className='py-2 pr-3 whitespace-nowrap'>{AI_ACTION_LABELS[f.action_type] || f.action_type}</td>
                  <td className='py-2 pr-3 max-w-md truncate text-slate-700' title={detalle(f)}>
                    {detalle(f)}
                  </td>
                  <td className='py-2 pr-3 whitespace-nowrap text-slate-600'>
                    {DECISIONES[f.decision] || f.decision}
                    {f.reviewer_name ? ` · ${f.reviewer_name}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {paginas > 1 && (
        <div className='flex items-center gap-2 text-sm'>
          <button
            type='button'
            disabled={pagina <= 1}
            onClick={() => setPagina(p => p - 1)}
            className='rounded-lg border border-slate-300 px-3 py-1 disabled:opacity-50'
          >
            Anterior
          </button>
          <span className='text-slate-500'>
            {pagina} / {paginas}
          </span>
          <button
            type='button'
            disabled={pagina >= paginas}
            onClick={() => setPagina(p => p + 1)}
            className='rounded-lg border border-slate-300 px-3 py-1 disabled:opacity-50'
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};

export default ArHistory;
